import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
} from "date-fns";
import { fr } from "date-fns/locale";
import { publicAPI, bookingAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import SessionDetailModal from "../components/SessionDetailModal";

const Planning = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? parseISO(dateParam) : new Date();
  });
  const [view, setView] = useState(() => searchParams.get('view') || 'week');
  const [selectedLocation, setSelectedLocation] = useState(() => searchParams.get('location') || 'all');
  const [selectedCoach, setSelectedCoach] = useState(() => searchParams.get('coach') || 'all');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');

  // API data states
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [selectedSession, setSelectedSession] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isAuthenticated, user } = useAuth();

  // Update URL params when filters change
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  // Helper functions to update state and URL
  const handleDateChange = (newDate) => {
    setCurrentDate(newDate);
    updateUrlParams({ date: format(newDate, 'yyyy-MM-dd') });
  };

  const handleViewChange = (newView) => {
    setView(newView);
    updateUrlParams({ view: newView });
  };

  const handleLocationChange = (newLocation) => {
    setSelectedLocation(newLocation);
    updateUrlParams({ location: newLocation });
  };

  const handleCoachChange = (newCoach) => {
    setSelectedCoach(newCoach);
    updateUrlParams({ coach: newCoach });
  };

  const handleSearchChange = (newSearch) => {
    setSearchTerm(newSearch);
    updateUrlParams({ search: newSearch });
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Prepare session filters based on view
        const sessionFilters = {};
        if (view === "week") {
          // Send the start date of the selected week
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
          sessionFilters.week_start = formatDate(weekStart);
        } else {
          sessionFilters.date = formatDate(currentDate);
        }

        // Add coach and location filters
        if (selectedCoach && selectedCoach !== 'all') {
          sessionFilters.coach_id = selectedCoach;
        }
        if (selectedLocation && selectedLocation !== 'all') {
          sessionFilters.location_id = selectedLocation;
        }
        if (searchTerm && searchTerm.trim() !== '') {
          sessionFilters.search = searchTerm.trim();
        }

        // Fetch locations, coaches, and sessions in parallel
        const [locationsData, coachesData, sessionsData] = await Promise.all([
          publicAPI.getLocations(),
          publicAPI.getCoaches(),
          publicAPI.getSessions(sessionFilters),
        ]);

        // Extract results from paginated API response
        setLocations(locationsData.results || locationsData);
        setCoaches(coachesData.results || coachesData);
        // For week view, API returns array directly, for other views it returns paginated response
        const finalSessions = Array.isArray(sessionsData)
          ? sessionsData
          : sessionsData.results || sessionsData;
        setSessions(finalSessions);
      } catch (error) {
        toast.error("Erreur lors du chargement des données");
        console.error("Error fetching data:", error);
        // Set empty arrays as fallback
        setLocations([]);
        setCoaches([]);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [view, currentDate, selectedCoach, selectedLocation, searchTerm]);

  const timeSlots = [
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
    "23:00",
  ];

  // Get week days for week view
  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start from Monday

    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  };

  // Format date for display
  const formatDate = (date) => {
    // Use local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get sessions for a specific date and time
  const getSessionsForSlot = (date, time) => {
    const dateStr = formatDate(date);
    if (!Array.isArray(sessions)) {
      return [];
    }

    // Convert time format: "08:00" to "08:00:00" to match API format
    const timeWithSeconds = time + ":00";
    const sessionsInSlot = sessions.filter(
      (s) => s.date === dateStr && s.start_time === timeWithSeconds
    );

    // Debug log for first slot
    if (dateStr === "2025-07-15" && time === "08:00") {
      console.log("Looking for sessions:", { dateStr, timeWithSeconds });
      console.log("Found sessions:", sessionsInSlot);
      console.log("Total sessions:", sessions.length);
      console.log(
        "Sessions for this date:",
        sessions.filter((s) => s.date === dateStr).length
      );
    }

    return sessionsInSlot;
  };

  // Sessions are now filtered by the API, no need for frontend filtering
  const filteredSessions = Array.isArray(sessions) ? sessions : [];

  // Navigation functions
  const goToPreviousDay = () => {
    handleDateChange(subDays(currentDate, 1));
  };

  const goToNextDay = () => {
    handleDateChange(addDays(currentDate, 1));
  };

  const goToPreviousWeek = () => {
    handleDateChange(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    handleDateChange(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    handleDateChange(new Date());
  };

  // Modal functions
  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSession(null);
  };

  const handleBookingSuccess = () => {
    // Refresh sessions data after successful booking
    const fetchSessions = async () => {
      try {
        const sessionFilters = {};
        if (view === "week") {
          // Send the start date of the selected week
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
          sessionFilters.week_start = formatDate(weekStart);
        } else {
          sessionFilters.date = formatDate(currentDate);
        }

        // Add coach and location filters
        if (selectedCoach && selectedCoach !== 'all') {
          sessionFilters.coach_id = selectedCoach;
        }
        if (selectedLocation && selectedLocation !== 'all') {
          sessionFilters.location_id = selectedLocation;
        }
        if (searchTerm && searchTerm.trim() !== '') {
          sessionFilters.search = searchTerm.trim();
        }

        const sessionsData = await publicAPI.getSessions(sessionFilters);
        setSessions(
          Array.isArray(sessionsData)
            ? sessionsData
            : sessionsData.results || sessionsData
        );
      } catch (error) {
        console.error("Error refreshing sessions:", error);
      }
    };

    fetchSessions();
  };

  // Get status color based on session status
  const getStatusColor = (session) => {
    if (!session) return "bg-gray-100 border-gray-200";

    if (session.can_reserve) {
      return "bg-green-50 border-green-200";
    } else if (session.can_waitlist) {
      return "bg-yellow-50 border-yellow-200";
    } else if (session.status === "booked") {
      return "bg-primary-50 border-primary-200";
    } else {
      return "bg-gray-100 border-gray-200";
    }
  };

  // Get status text based on session status
  const getStatusText = (session) => {
    if (!session) return "Disponible";

    if (session.can_reserve) {
      return "Disponible";
    } else if (session.can_waitlist) {
      return `Complet + ${session.waitlist_count || 0} en attente`;
    } else if (session.status === "booked") {
      return "Complet";
    } else {
      return "Non disponible";
    }
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              Planning des Séances
            </h1>
            <p className="mt-2 text-sm md:text-base text-gray-600">
              Consultez les disponibilités et réservez votre séance
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            {/* View Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => handleViewChange("day")}
                  className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    view === "day"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Jour
                </button>
                <button
                  onClick={() => handleViewChange("week")}
                  className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    view === "week"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Semaine
                </button>
              </div>

              <button
                onClick={goToToday}
                className="sm:ml-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Aujourd'hui
              </button>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center space-x-4">
              <button
                onClick={view === "day" ? goToPreviousDay : goToPreviousWeek}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>

              <h2 className="text-base md:text-lg font-semibold text-gray-900 min-w-[150px] md:min-w-[200px] text-center">
                {view === "week"
                  ? `${format(getWeekDays()[0], "d MMM", {
                      locale: fr,
                    })} - ${format(getWeekDays()[6], "d MMM yyyy", {
                      locale: fr,
                    })}`
                  : format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
              </h2>

              <button
                onClick={view === "day" ? goToNextDay : goToNextWeek}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
              Filtres
              {showFilters ? " -" : " +"}
            </button>
          </div>

          {/* Filters */}
          {1 === 1 && (
            // {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="location"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Studio
                </label>
                <select
                  id="location"
                  value={selectedLocation}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">Tous les studios</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="coach"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Coach
                </label>
                <select
                  id="coach"
                  value={selectedCoach}
                  onChange={(e) => handleCoachChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">Tous les coachs</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {coach.user.first_name} {coach.user.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Recherche
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="search"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div> */}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {view === "week" && (
            <div className="overflow-x-auto">
              <div className="min-w-[1200px] md:min-w-[1200px]">
                {/* Header */}
                <div className="grid grid-cols-8 border-b border-gray-200">
                  <div className="p-2 md:p-4 bg-gray-50 font-medium text-gray-700 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2" />
                    Heure
                  </div>
                  {getWeekDays().map((day, index) => (
                    <div
                      key={index}
                      className={`p-2 md:p-4 text-center text-sm md:text-base ${
                        isSameDay(day, new Date())
                          ? "bg-primary-50"
                          : "bg-gray-50"
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        {format(day, "EEE", { locale: fr })}
                      </div>
                      <div
                        className={`text-sm ${
                          isSameDay(day, new Date())
                            ? "text-primary-600 font-medium"
                            : "text-gray-500"
                        }`}
                      >
                        {format(day, "d MMM", { locale: fr })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Time Slots */}
                {timeSlots.map((time, index) => (
                  <div
                    key={time}
                    index={index}
                    className={`grid grid-cols-8 border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50" : ""}`}
                  >
                    {/* Time */}
                    <div className="p-2 md:p-4 bg-gray-50 font-medium text-gray-700 flex items-center text-sm md:text-base">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      {time}
                    </div>
                    {/* Days  */}
                    {getWeekDays().map((day, dayIndex) => {
                      const sessionsInSlot = getSessionsForSlot(day, time);
                      const filteredSessionsInSlot = sessionsInSlot.filter(
                        (session) =>
                          filteredSessions.some((s) => s.id === session.id)
                      );

                      return (
                        <div
                          key={dayIndex}
                          className="p-1 md:p-2 border-r border-gray-100 min-h-[100px] md:min-h-[120px]"
                        >
                          {filteredSessionsInSlot.length > 0 ? (
                            <div className="space-y-1 h-full">
                              {filteredSessionsInSlot.map(
                                (session, sessionIndex) => (
                                  <button
                                    key={session.id}
                                    onClick={() => handleSessionClick(session)}
                                    className={`p-2 md:p-3 rounded-lg border text-xs flex flex-col ${getStatusColor(
                                      session
                                    )} hover:shadow-md transition-shadow cursor-pointer w-full text-left`}
                                  >
                                    {session.title && (
                                      <div className="font-bold text-sm  mb-1 text-primary-700">
                                        {session.title}
                                      </div>
                                    )}
                                    <div className="text-[10px] text-gray-600 flex items-center mb-1 line-clamp-1 w-max overflow-hidden">
                                      <MapPinIcon className="h-3 w-3 mr-1 " />
                                      {session.location.name}
                                    </div>
                                    <div className="font-medium text-gray-900 mb-1 text-[8px]">
                                      {session.coach.user.full_name}
                                    </div>

                                    <div className="mt-auto flex items-center justify-between text-[6px]">
                                      <div className="flex items-center text-primary-600">
                                        <UsersIcon className="h-2 w-2 mr-1" />
                                        {session.current_bookings}/
                                        {session.max_capacity}
                                      </div>
                                      {session.waitlist_count > 0 && (
                                        <div className="flex items-center text-yellow-600 text-xs">
                                          <ClipboardDocumentListIcon className="h-2 w-2 mr-1" />
                                          {session.waitlist_count}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-xs mt-1 text-gray-500">
                                      {getStatusText(session)}
                                    </div>
                                  </button>
                                )
                              )}
                            </div>
                          ) : (
                            <div className="h-full flex items-center text-center justify-center text-gray-400 text-xs border border-dashed border-gray-200 rounded-lg p-2">
                              Aucune séance programmée
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === "day" && (
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
              </h3>

              <div className="space-y-3">
                {timeSlots.map((time) => {
                  const sessionsInSlot = getSessionsForSlot(currentDate, time);
                  const filteredSessionsInSlot = sessionsInSlot.filter(
                    (session) =>
                      filteredSessions.some((s) => s.id === session.id)
                  );

                  return (
                    <div
                      key={time}
                      className="flex border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div className="w-16 sm:w-24 bg-gray-50 p-2 sm:p-4 flex items-center justify-center font-medium text-gray-700 text-sm sm:text-base">
                        {time}
                      </div>
                      <div className="flex-1 p-4">
                        {filteredSessionsInSlot.length > 0 ? (
                          <div className="space-y-2">
                            {filteredSessionsInSlot.map(
                              (session, sessionIndex) => (
                                <button
                                  key={session.id}
                                  onClick={() => handleSessionClick(session)}
                                  className={`p-4 md:p-5 rounded-lg border ${getStatusColor(
                                    session
                                  )} hover:shadow-md transition-shadow cursor-pointer w-full text-left`}
                                >
                                  {session.title && (
                                    <div className="font-bold text-base md:text-lg mb-2 text-primary-700">
                                      {session.title}
                                    </div>
                                  )}
                                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                    <div>
                                      <div className="font-medium text-gray-900 flex items-center">
                                        <UserIcon className="h-4 w-4 mr-2" />
                                        {session.coach.user.full_name}
                                      </div>
                                      <div className="text-sm text-gray-600 flex items-center mt-1">
                                        <MapPinIcon className="h-4 w-4 mr-2" />
                                        {session.location.name}
                                      </div>
                                    </div>

                                    <div className="mt-3 md:mt-0 flex items-center space-x-4">
                                      <div className="flex items-center text-primary-600">
                                        <UsersIcon className="h-4 w-4 mr-1" />
                                        <span className="font-medium">
                                          {session.current_bookings}/
                                          {session.max_capacity}
                                        </span>
                                      </div>
                                      {session.waitlist_count > 0 && (
                                        <div className="flex items-center text-yellow-600">
                                          <ClipboardDocumentListIcon className="h-4 w-4 mr-1" />
                                          <span className="font-medium">
                                            Liste d'attente:{" "}
                                            {session.waitlist_count}
                                          </span>
                                        </div>
                                      )}
                                      <div className="text-sm font-medium">
                                        {getStatusText(session)}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              )
                            )}
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-lg p-4">
                            Aucune séance programmée
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Légende</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-50 border border-green-200 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Disponible</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-primary-50 border border-primary-200 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Complet</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-50 border border-yellow-200 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Liste d'attente</span>
            </div>
          </div>
        </div>
      </div>

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onBookingSuccess={handleBookingSuccess}
      />
    </div>
  );
};

export default Planning;
